import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Card, Grid, FormControl, InputLabel, Select, MenuItem, TextField, Divider } from '@mui/material'
import { CurrencySelect } from '../../currencies/components/CurrencySelect'
import { Currency } from '../../currencies/models/Currency'
import { DateDayControl } from 'src/admin/core/components/date/DateDayControl'
import { Agency, Enterprise, WorkstationProductsEnum } from 'src/core/models'
import { AgencySelect, UserAgenciesSelect, WorkstationProductSelect } from 'src/admin/core'
import { useAppTranslation } from 'src/admin/core/hooks'
import { saveAs } from 'file-saver'
import { DownloadButton, SearchButton, ProviderSelect } from 'src/admin/core'
import {
  dateIsBeforeThanTodayMoment,
  formatJSDateToLocal,
  isFutureDate,
  isSameDate,
} from 'src/admin/core/services/dateFunctions'

import { getAppBarState } from 'src/store'
import { UserEnterpriseSelect } from 'src/admin/core/components/Select/UserEnterprisesSelect'
import { useLotsalcomFileDownloadUri } from '../../agencyBalance/hooks'
 

export interface FilterModel {
  product: string
  locationId: string | undefined
  currencyId: string
  startDate: string
  endDate: string
  agencies: Agency[]
  provider?: string
  enterprise?: Enterprise
  workstation?: string
}

export const today = new Date()

const { userEnterprise } = getAppBarState().appState

export const initialFilters = {
  product: 'HORSE_RACES',
  currencyId: 'DOP',
  locationId: undefined,
  startDate: formatJSDateToLocal(today),
  endDate: formatJSDateToLocal(today),
  agencies: [],
  provider: '',
  enterprise: undefined,
  workstation: '',
}

export const Filters: React.FC<{ onChange: (filters: FilterModel) => void; onSearch: (filters: FilterModel) => void; isLoading?: boolean }> = ({ onChange, onSearch, isLoading = false }) => {
  const [filters, setFilters] = useState<FilterModel>(initialFilters)
  const { getLotsalcomFileDownloadUri, file } = useLotsalcomFileDownloadUri()
  const [readyToDownload, setReadyToDownload] = useState(true)
  const [isDefaultEnterprise, setIsDefaultEnterprise] = useState(false)
  const [workstationInput, setWorkstationInput] = useState('')
  const prevWorkstationInputRef = useRef('')
  

  const { label } = useAppTranslation()
  const startDate = filters.startDate
  const { userEnterprise } = getAppBarState().appState
  const enterprise = filters.enterprise || userEnterprise

  // No initial filtering - all filtering is done through search button

  // Debounce workstation filter - update filters after user stops typing
  useEffect(() => {
    // Only run if workstationInput actually changed
    if (prevWorkstationInputRef.current === workstationInput) {
      return
    }
    
    prevWorkstationInputRef.current = workstationInput
    
    const timeoutId = setTimeout(() => {
      setFilters(currentFilters => {
        const updatedFilters = {
          ...currentFilters,
          workstation: workstationInput
        }
        // Call onChange to update parent for local filtering
        onChange(updatedFilters)
        return updatedFilters
      })
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [workstationInput, onChange])

  useEffect(() => {
    if (file && startDate && readyToDownload) {
      saveAs(file.sasUri, file.fileName)
    }
  }, [file, startDate, readyToDownload])

  const handleStartDateChange = (date: Date) => {
    setReadyToDownload(false)
    setFilters({
      ...filters,
      startDate: formatJSDateToLocal(date),
    })
  }

  const handleEndDateChange = (date: Date) => {
    setReadyToDownload(false)
    setFilters({
      ...filters,
      endDate: formatJSDateToLocal(date),
    })
  }

  const handleDownlaod = useCallback((date: string) => {
    getLotsalcomFileDownloadUri(date)
    setReadyToDownload(true)
  }, [])

  const handleSearch = useCallback(() => {
    // Clear the workstation input
    setWorkstationInput('')
    
    // Use functional update to get the latest state
    setFilters(currentFilters => {
      // Clear the workstation filter when searching
      const filtersForSearch = {
        ...currentFilters,
        workstation: ''
      }
      // Send filters to parent and trigger search with the updated filters
      onChange(filtersForSearch)
      onSearch(filtersForSearch)
      return filtersForSearch
    })
  }, [onChange, onSearch])

  // No agency fetching needed for simple TextField input
  
  return (
    <Card
      sx={{
        p: 1,
      }}
    >
      <Box p={1}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2} lg={2}>
            <DateDayControl onChange={handleStartDateChange} showButtons={false} label={label('FECHA_DESDE')} />
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <DateDayControl onChange={handleEndDateChange} showButtons={false} label={label('FECHA_HASTA')} />
          </Grid>          
          <Grid item xs={12} md={2} lg={2}>
            <WorkstationProductSelect
              disableClearable={false}
              selectDefaultProduct={false}
              value={filters.product as WorkstationProductsEnum}
              onChange={(val) => {
                setFilters({
                  ...filters,
                  product: val || 'HORSE_RACES',
                });
              }}
              getDefaultValue={(val) => {
                setFilters({
                  ...filters,
                  product: val || 'HORSE_RACES',
                });
              }}
            />
          </Grid>
          {!isDefaultEnterprise && (
            <Grid item xs={12} md={2} lg={2}>
              <UserEnterpriseSelect selectDefaultEnterprise={false} onChangeValue={(value: Enterprise, action: string) => {
                  if (action === 'default') {
                    setIsDefaultEnterprise(true);
                    setFilters({
                      ...filters,
                      enterprise: value,
                      provider: '', // Set provider to empty string (TODOS) when enterprise is default
                    });
                  } else {
                    setIsDefaultEnterprise(false);
                    setFilters({
                      ...filters,
                      enterprise: value,
                    });
                  }
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={2} lg={2}>
            <AgencySelect
              selectedEnterprise={filters.enterprise}
              disableClearable={false}
              onChangeValue={(value: Agency) => {
                setFilters({
                  ...filters,
                  locationId: value?.id,
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
              <ProviderSelect
                disableClearable={false}
                value={filters.provider}
                onChange={(val) => {
                  setFilters({
                    ...filters,
                    provider: val || '',
                  });
                }}
                getDefaultValue={(val) => {
                  setFilters({
                    ...filters,
                    provider: val || '',
                  });
                }}
              />
            </Grid>      
          <Grid item xs={12} md={2} lg={2}>
            <SearchButton 
              color="primary" 
              onClick={handleSearch} 
              isLoading={isLoading}
              style={{ height: '56px', width: '100%' }}
            />
          </Grid>
          
          {/* Vertical Divider */}
          <Grid item xs={12} md={0.1} lg={0.1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Divider orientation="vertical" flexItem sx={{ height: '56px' }} />
          </Grid>
          
          {/* ESTACION filter on the same row as search button */}
          <Grid item xs={12} md={3.9} lg={3.9}>
            <TextField
              label={label('ESTACION')}
              variant="outlined"
              fullWidth
              value={workstationInput}
              onChange={(e) => setWorkstationInput(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              placeholder={label('BUSCAR_ESTACION')}
            />
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}
